import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import IndustrySolutionCard from '../../components/IndustrySolutionCard';
import SEO from '../../components/SEO';

const Fintech = () => {
  const serviceTerms = [
    'Payment Processing', 'Fraud Detection', 'Risk Analytics', 'Demand Forecasting',
    'Inventory Optimisation', 'Digital Commerce', 'Checkout Automation', 'ML-Powered Insights',
    'Subscription Management', 'Customer Segmentation', 'Revenue Analytics', 'Order Fulfilment',
    'Dynamic Pricing', 'Product Recommendations', 'Compliance Automation', 'Multi-Currency Payments'
  ];

  const solutions = [
    {
      icon: <i className="fi fi-ss-credit-card text-4xl"></i>,
      title: 'Payment Automation & Reconciliation',
      description: 'Automate checkout, settlement, and reconciliation across channels with secure and compliant transaction processing.',
      features: serviceTerms.slice(0, 4)
    },
    {
      icon: <i className="fi fi-ss-shield-check text-4xl"></i>,
      title: 'Commerce Risk & Fraud Intelligence',
      description: 'Detect anomalies in transaction behaviour and reduce fraud exposure with real-time risk scoring pipelines.',
      features: serviceTerms.slice(4, 8)
    },
    {
      icon: <i className="fi fi-ss-chart-line-up text-4xl"></i>,
      title: 'Demand & Revenue Analytics',
      description: 'Forecast demand, optimise inventory, and improve margins using data-driven pricing and recommendation systems.',
      features: serviceTerms.slice(8, 12)
    },
    {
      icon: <i className="fi fi-ss-globe text-4xl"></i>,
      title: 'Global Commerce Enablement',
      description: 'Scale internationally with robust compliance workflows, localisation, and multi-currency payment support.',
      features: serviceTerms.slice(12, 16)
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <SEO
        title="Ecommerce Solutions — Fintech & Digital Commerce | STRAY Apps"
        description="End-to-end digital commerce platforms with intelligent analytics, payment automation, and ML-powered demand forecasting."
        path="/industry-solutions/fintech"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="text-left mb-8">
            <h1 className="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-4">
              Ecommerce Solutions
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl font-light">
              End-to-end digital commerce platforms with intelligent analytics, payment automation, and ML-powered demand forecasting.
            </p>
          </div>
        </div>
      </section>
{/* Solutions Grid */}
      <section className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <IndustrySolutionCard
                key={index}
                icon={solution.icon}
                title={solution.title}
                description={solution.description}
                features={solution.features}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Forecast Engine Feature Card */}
      <section className="py-16 bg-gradient-to-b from-white via-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-2 border-purple-200 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* Left - Content */}
              <div className="p-8 sm:p-10">
                <span className="inline-block px-3 py-1 text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-full mb-4">POC Demo</span>
                <h2 className="text-3xl font-bold text-gray-800 mb-3">Forecast Engine</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  ML-powered demand forecasting for inventory management. Uses Facebook Prophet for time-series prediction with seasonality detection and promotional impact analysis.
                </p>
                <ul className="space-y-2 mb-8">
                  {[
                    'Demand prediction with confidence intervals',
                    'Promotional impact detection',
                    'Automated reorder recommendations',
                    'Forecast quality metrics (MAPE, MAE, RMSE)'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <ArrowRight className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/apps/forecast-demo">
                  <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg text-white font-semibold transition-all hover:shadow-lg hover:shadow-purple-500/30">
                    Try Demo <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>
              
              {/* Right - Visual */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-10">
                <img
                  src="/images/forecastapp.png"
                  alt="Forecast Engine app preview"
                  className="w-full max-w-lg rounded-xl border border-purple-200 shadow-md object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Gift Platform Card */}
      <section className="pb-16 bg-gradient-to-b from-white via-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-2 border-purple-200 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-8 sm:p-10">
                <span className="inline-block px-3 py-1 text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-full mb-4">Live Project</span>
                <h2 className="text-3xl font-bold text-gray-800 mb-3">AI Gift Commerce Platform</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Full-stack AI-powered recommendation and e-commerce platform. Users create sessions, add recipients with preferences, and receive personalised product suggestions generated via LLM — then checkout seamlessly with integrated payments.
                </p>
                <ul className="space-y-2 mb-8">
                  {[
                    'AI-generated personalised recommendations per recipient',
                    'Multi-recipient session management with stage tracking',
                    'Stripe payment processing with saved cards',
                    'Bulk recipient upload via spreadsheet',
                    'Full order history and activity audit logging'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <ArrowRight className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-10">
                <img
                  src="/images/giftapp.png"
                  alt="AI Gift platform preview"
                  className="w-full max-w-lg rounded-xl border border-purple-200 shadow-md object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Ready to Transform Your Ecommerce Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how our solutions can help you scale and optimise.
          </p>
          <Link to="/contact">
            <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg text-white font-semibold text-lg transition-all hover:shadow-lg hover:shadow-purple-500/30">
              Get in Touch
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Fintech;
